import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import PageFooter from '@/components/feature/PageFooter';
import QuickBuySection from './components/QuickBuySection';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

interface ErpPointResult {
  user_id: string;
  member_name?: string;
  point: number;
  status: 'success' | 'error';
  message?: string;
}

type QueryState = 'idle' | 'loading' | 'success' | 'error';

export default function MyPointsPage() {
  const [memberId, setMemberId] = useState('');
  const [queryState, setQueryState] = useState<QueryState>('idle');
  const [result, setResult] = useState<ErpPointResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showEndpointInfo, setShowEndpointInfo] = useState(false);
  const [copiedEndpoint, setCopiedEndpoint] = useState(false);

  const erpEndpointUrl = `${SUPABASE_URL}/functions/v1/erp-points`;
  const formatPrice = (n: number) =>
    parseFloat(n.toFixed(2)).toLocaleString('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handleCheckBalance = useCallback(async () => {
    const inputVal = memberId.trim();
    if (!inputVal) {
      setError('회원ID를 입력해주세요.');
      return;
    }
    setError(null);
    setQueryState('loading');
    setResult(null);

    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/erp-points`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_KEY,
          'Authorization': `Bearer ${SUPABASE_KEY}`,
        },
        body: JSON.stringify({ user_code: inputVal, mode: 'query' }),
      });

      const data = await res.json();

      if (data.success === 1) {
        setResult({
          user_id: data.user_id || inputVal,
          member_name: data.member_name || '',
          point: data.point != null ? parseFloat(String(data.point)) : 0,
          status: 'success',
        });
        setQueryState('success');
      } else {
        setResult({
          user_id: inputVal,
          point: 0,
          status: 'error',
          message: data.msg || '포인트 조회에 실패했습니다.',
        });
        setQueryState('error');
      }
    } catch (err) {
      setResult({
        user_id: inputVal,
        point: 0,
        status: 'error',
        message: '서버 연결에 실패했습니다. 잠시 후 다시 시도해주세요.',
      });
      setQueryState('error');
    }
  }, [memberId]);

  const handleReset = () => {
    setQueryState('idle');
    setResult(null);
    setMemberId('');
    setError(null);
  };

  const handleCopyEndpoint = () => {
    navigator.clipboard.writeText(erpEndpointUrl).then(() => {
      setCopiedEndpoint(true);
      setTimeout(() => setCopiedEndpoint(false), 2000);
    });
  };

  return (
    <>
      <Navbar />

      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 pt-20 pb-16">
        {/* 헤더 배너 */}
        <div className="relative overflow-hidden h-[220px] md:h-[280px]">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/decfaa9e67ddbf0507b7bf4a8f6a2087.jpg"
              alt="내 포인트 배너"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/85 via-emerald-800/70 to-teal-800/60"></div>
          </div>
          <div className="relative max-w-4xl mx-auto px-4 md:px-8 py-10 md:py-14 text-white">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-emerald-400/30 text-emerald-100 border border-emerald-400/40">
                ERP 포인트 연동
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-white/10 text-white border border-white/20">
                Global AI Trade
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: 'Noto Serif KR, serif' }}>
              내 쇼핑 포인트
            </h1>
            <p className="text-emerald-100 text-sm md:text-base leading-relaxed">
              전산 시스템에서 적립된 포인트를 확인하고 상품 구매에 사용하세요.
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 md:px-8 py-10 space-y-6">

          {/* ── 조회 입력 카드 ── */}
          <div className="bg-white rounded-2xl p-6 md:p-8">
            <h2 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2" style={{ fontFamily: 'Noto Serif KR, serif' }}>
              <i className="ri-id-card-line w-5 h-5 flex items-center justify-center text-emerald-600"></i>
              회원ID로 포인트 조회
            </h2>
            <p className="text-sm text-gray-500 mb-5">
              전산 회원ID (예: MEM001)를 입력하시면 포인트 잔액을 바로 확인할 수 있습니다.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <input
                  type="text"
                  value={memberId}
                  onChange={(e) => { setMemberId(e.target.value); setError(null); }}
                  onKeyDown={(e) => e.key === 'Enter' && queryState !== 'loading' && handleCheckBalance()}
                  placeholder="회원ID 입력 (예: MEM001)"
                  disabled={queryState === 'loading'}
                  className={`w-full px-4 py-3 border-2 rounded-xl text-sm font-mono tracking-wider focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-colors disabled:bg-gray-50 disabled:text-gray-400 ${
                    error ? 'border-red-300' : 'border-gray-200 focus:border-emerald-400'
                  }`}
                />
                {error && (
                  <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                    <i className="ri-error-warning-line w-3.5 h-3.5 flex items-center justify-center"></i>
                    {error}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={queryState === 'loading' ? undefined : handleCheckBalance}
                disabled={queryState === 'loading'}
                className="px-6 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 flex items-center gap-2 justify-center"
              >
                {queryState === 'loading' ? (
                  <><i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>조회 중...</>
                ) : (
                  <><i className="ri-search-line w-4 h-4 flex items-center justify-center"></i>잔액 조회</>
                )}
              </button>
            </div>
          </div>

          {/* ── 로딩 상태 ── */}
          {queryState === 'loading' && (
            <div className="bg-white rounded-2xl p-10 flex flex-col items-center justify-center gap-4">
              <div className="relative w-20 h-20 flex items-center justify-center">
                <div className="absolute inset-0 rounded-full border-4 border-emerald-100"></div>
                <div className="absolute inset-0 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin"></div>
                <i className="ri-coin-line text-emerald-600 text-2xl"></i>
              </div>
              <div className="text-center">
                <p className="font-bold text-gray-800 text-base mb-1">포인트 조회 중...</p>
                <p className="text-sm text-gray-400">
                  <span className="font-mono font-bold text-emerald-600">{memberId}</span> 회원의 포인트를 확인하고 있습니다.
                </p>
              </div>
              <div className="flex gap-1.5 mt-2">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="w-2 h-2 rounded-full bg-emerald-400 animate-bounce"
                    style={{ animationDelay: `${i * 0.15}s` }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ── 성공 결과 ── */}
          {queryState === 'success' && result && (
            <>
              {/* 회원 + 포인트 요약 */}
              <div className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-2xl p-6 md:p-8 text-white">
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
                  {/* 회원 정보 */}
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-2xl flex-shrink-0">
                      <i className="ri-user-3-line text-white text-3xl"></i>
                    </div>
                    <div>
                      <p className="text-emerald-200 text-xs font-semibold mb-0.5">조회된 회원</p>
                      {result.member_name && (
                        <p className="text-base font-bold text-white mb-0.5">{result.member_name}</p>
                      )}
                      <p className="text-lg font-black font-mono tracking-wider">{result.user_id}</p>
                      <div className="flex items-center gap-1.5 mt-1.5">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-400/30 text-emerald-100 text-xs font-bold border border-emerald-400/40">
                          <i className="ri-checkbox-circle-fill w-3 h-3 flex items-center justify-center"></i>
                          조회 완료
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* 포인트 */}
                  <div className="text-center sm:text-right flex-shrink-0">
                    <p className="text-emerald-200 text-xs font-semibold mb-1">보유 포인트</p>
                    <p className="text-5xl md:text-6xl font-black leading-none">{formatPrice(result.point)}</p>
                    <p className="text-emerald-300 text-sm font-bold mt-1">P</p>
                  </div>
                </div>
              </div>

              {/* 포인트 요약 액션 바 */}
              <div className="bg-white rounded-2xl px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-sm text-gray-500">
                  아래에서 <span className="font-bold text-emerald-600">{formatPrice(result.point)}P</span>로 바로 구매할 수 있는 제품을 확인하세요
                </p>
                <button
                  type="button"
                  onClick={handleReset}
                  className="flex-shrink-0 px-4 py-2 border-2 border-gray-200 text-gray-500 rounded-full text-xs font-bold hover:border-gray-300 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5"
                >
                  <i className="ri-refresh-line w-3.5 h-3.5 flex items-center justify-center"></i>
                  다시 조회
                </button>
              </div>

              {/* 빠른 구매 섹션 */}
              <QuickBuySection pointBalance={result.point} userId={result.user_id} />

              {/* 포인트 충전 안내 */}
              {result.point === 0 && (
                <div className="bg-white rounded-2xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-xl flex-shrink-0">
                      <i className="ri-lightbulb-line text-amber-600 text-lg"></i>
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-sm mb-1">포인트 충전이 필요하신가요?</p>
                      <p className="text-xs text-gray-500 leading-relaxed mb-3">
                        포인트가 부족하다면 포인트 구매 페이지에서 충전하실 수 있습니다.
                      </p>
                      <Link
                        to="/point-purchase"
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-amber-500 text-white rounded-lg text-xs font-bold hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-add-circle-line w-3.5 h-3.5 flex items-center justify-center"></i>
                        포인트 구매하기
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── 에러 상태 ── */}
          {queryState === 'error' && result && (
            <div className="bg-white rounded-2xl p-8">
              <div className="flex flex-col items-center text-center mb-6">
                <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-full mb-4">
                  <i className="ri-error-warning-line text-red-500 text-3xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">포인트 조회 실패</h3>
                <p className="text-sm text-gray-500">{result.message || '전산 시스템 연결에 실패했습니다.'}</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                <p className="text-xs text-red-700 leading-relaxed">
                  회원ID를 다시 확인하거나, 잠시 후 재시도해주세요. 문제가 지속되면 고객센터로 문의해주세요.
                </p>
              </div>
              <button
                type="button"
                onClick={handleReset}
                className="w-full px-6 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-refresh-line w-4 h-4 flex items-center justify-center"></i>
                다시 시도하기
              </button>
            </div>
          )}

          {/* ── 초기 안내 (idle 상태) ── */}
          {queryState === 'idle' && (
            <div className="bg-white rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-xl flex-shrink-0">
                  <i className="ri-link-m text-amber-600 text-lg"></i>
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-sm mb-1">처음 이용하시나요? ERP 포인트 연동</p>
                  <p className="text-xs text-gray-500 leading-relaxed mb-3">
                    전산 회원이시면 위 입력창에 전산 회원ID를 입력하시면 포인트 잔액을 바로 확인하실 수 있습니다.
                  </p>
                  <Link
                    to="/erp-guide"
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-amber-500 text-white rounded-lg text-xs font-bold hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-question-line w-3.5 h-3.5 flex items-center justify-center"></i>
                    ERP 연동 안내 보기
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* ── 관리자용 ERP 엔드포인트 안내 ── */}
          <div className="bg-white rounded-2xl p-6 md:p-8">
            <button
              type="button"
              onClick={() => setShowEndpointInfo(!showEndpointInfo)}
              className="w-full flex items-center justify-between cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-gray-100 rounded-xl">
                  <i className="ri-settings-3-line text-gray-600 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div className="text-left">
                  <p className="text-sm font-bold text-gray-900">ERP 연동 설정 (관리자용)</p>
                  <p className="text-xs text-gray-400">전산 담당자에게 전달할 API 주소</p>
                </div>
              </div>
              <i className={`${showEndpointInfo ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-gray-400 w-5 h-5 flex items-center justify-center`}></i>
            </button>

            {showEndpointInfo && (
              <div className="mt-5 space-y-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs font-bold text-gray-700 mb-3 flex items-center gap-1.5">
                    <i className="ri-code-s-slash-line w-4 h-4 flex items-center justify-center text-emerald-600"></i>
                    전산 프로그램 &rarr; 쇼핑몰 포인트 전달 주소 (price.php 대체)
                  </p>
                  <div className="bg-white border-2 border-emerald-200 rounded-lg px-3 py-3 mb-2">
                    <code className="text-xs font-mono text-emerald-700 break-all select-all">{erpEndpointUrl}</code>
                  </div>
                  <button
                    type="button"
                    onClick={handleCopyEndpoint}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                      copiedEndpoint ? 'bg-emerald-500 text-white' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                    }`}
                  >
                    {copiedEndpoint ? (
                      <><i className="ri-check-line w-3 h-3 flex items-center justify-center"></i>복사됨!</>
                    ) : (
                      <><i className="ri-file-copy-line w-3 h-3 flex items-center justify-center"></i>주소 복사</>
                    )}
                  </button>
                </div>

                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <p className="text-xs font-bold text-emerald-800 mb-2 flex items-center gap-1.5">
                    <i className="ri-information-line w-3.5 h-3.5 flex items-center justify-center"></i>
                    전산 담당자 전달 내용
                  </p>
                  <div className="bg-white rounded-lg p-3 font-mono text-xs text-gray-700 space-y-1.5 border border-emerald-100">
                    <p className="text-gray-400">// POST 방식, JSON 형식으로 전달</p>
                    <p className="text-emerald-700">curl_setopt($ch, CURLOPT_URL, <span className="text-emerald-600">&quot;{erpEndpointUrl}&quot;</span>);</p>
                    <p className="text-gray-400">// 전달 데이터</p>
                    <p>$post_fields[<span className="text-orange-600">&quot;user_code&quot;</span>] = $member_row[ShopUserCode];</p>
                    <p>$post_fields[<span className="text-orange-600">&quot;price&quot;</span>] = $DepositPayRealQuantity;</p>
                    <p>$post_fields[<span className="text-orange-600">&quot;mode&quot;</span>] = <span className="text-green-600">&quot;save&quot;</span>;</p>
                    <p>$post_fields[<span className="text-orange-600">&quot;test&quot;</span>] = <span className="text-green-600">&quot;N&quot;</span>;</p>
                    <p className="text-gray-400">// 응답: &lbrace;&quot;success&quot;:1, &quot;msg&quot;:&quot;...&quot;&rbrace;</p>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-xs font-bold text-amber-800 mb-2 flex items-center gap-1.5">
                    <i className="ri-alert-line w-3.5 h-3.5 flex items-center justify-center"></i>
                    user_code 규칙
                  </p>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    전산 프로그램에서 ShopUserCode를 저장할 때 <strong>회원의 전화번호(하이픈 제거)</strong>를 사용해주세요.<br />
                    예: 010-1234-5678 → <code className="font-mono bg-amber-100 px-1 rounded">01012345678</code>
                  </p>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
      <PageFooter />
    </>
  );
}
